import { find, get } from 'lodash';
import { useRouter } from 'next/router';
import React, {
  Dispatch,
  Reducer,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  PropsWithChildren
} from 'react';

import { IProduct, IProductVariations } from '@/models';

export enum ActionKind {
  INIT = 'INIT',
  SET_SIZE = 'SET_SIZE',
  SET_COLOR = 'SET_COLOR'
}

type Action =
  | {
      type: ActionKind.INIT;
      payload: Store;
    }
  | {
      type: ActionKind.SET_SIZE;
      payload: string;
    }
  | {
      type: ActionKind.SET_COLOR;
      payload: string;
    };

interface Store {
  size: string;
  color: string;
}

interface State extends Store {
  product: IProduct;
  variation: IProductVariations | undefined;
}

function reducer(state: Store, action: Action): Store {
  const { type, payload } = action;

  switch (type) {
    case ActionKind.INIT:
      return { size: payload.size, color: payload.color };

    case ActionKind.SET_SIZE:
      return { ...state, size: payload };

    case ActionKind.SET_COLOR:
      return { color: payload, size: '' };

    default:
      return state;
  }
}

const ProductState = createContext<State>({
  size: '',
  color: '',
  product: {} as IProduct,
  variation: undefined
});

const ProductDispatch = createContext<Dispatch<Action>>(() => {});

interface ProductProps extends PropsWithChildren {
  product: IProduct;
}

export const ProductStoreProvider: React.FC<ProductProps> = ({
  product,
  children
}) => {
  const router = useRouter();

  const initialState: Store = {
    size: '',
    color: get(
      find(
        product.colors,
        ({ color_code }) => color_code === router.query.color
      ),
      'color_code',
      product.colors[0].color_code
    )
  };

  const [state, dispatch] = useReducer<Reducer<Store, Action>>(
    reducer,
    initialState
  );

  useEffect(() => {
    dispatch({ type: ActionKind.INIT, payload: initialState });
  }, [product, router.query.color]);

  const variation = useMemo(() => {
    return product.variations.find(({ attribute_size, attribute_color }) => {
      return attribute_size === state.size && attribute_color === state.color;
    });
  }, [product, state]);

  const value = useMemo(
    () => ({
      ...state,
      product,
      variation
    }),
    [product, state, variation]
  );

  return (
    <ProductState.Provider value={value}>
      <ProductDispatch.Provider value={dispatch}>
        {children}
      </ProductDispatch.Provider>
    </ProductState.Provider>
  );
};

export const useProductState = () => useContext(ProductState);
export const useProductDispatch = () => useContext(ProductDispatch);
